import React, { MouseEventHandler, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { AdacButton } from '@adac-de/component-library';
import { clsx } from 'clsx';
import styles from './modal.module.scss';
import { Icon } from '@components/icons/icons';
import { useAnimateTransition } from '@hooks/use-animate-transition';

export type ModalProps = {
  isOpen: boolean;
  close: MouseEventHandler<HTMLElement>;
  modalRef?: RefObject<HTMLDivElement>;
  inverse?: boolean;
  className?: string;
  size?: 'half' | 'full';
  headerContent?: React.ReactNode;
};

const Modal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  isOpen,
  close,
  modalRef,
  inverse,
  children,
  size = 'half',
  headerContent
}) => {
  const { stage, shouldMount } = useAnimateTransition(isOpen, 300);

  return shouldMount && modalRef?.current
    ? createPortal(
        <div
          className={clsx({
            [styles[size]]: true,
            [styles.show]: stage === 'enter'
          })}
          tabIndex={-1}
          role="dialog"
          onClick={close}
        >
          <div
            className={clsx({
              [styles.modalDialog]: true,
              [styles.modalDialogInverse]: inverse
            })}
            role="document"
            onClick={e => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              {headerContent}
              <AdacButton
                variant="unstyled"
                aria-label="Schließen"
                onClick={close}
                data-testid="close"
                className={styles.modalClose}
              >
                <Icon.Close />
              </AdacButton>
            </div>

            <div className={styles.modalContent}>{children}</div>
          </div>
        </div>,
        modalRef.current
      )
    : null;
};

export default Modal;
